import { MotionProps } from "framer-motion"
import { ComponentType, ReactNode } from "react"
import { twMerge } from "tailwind-merge"
import { LoadingResults } from "./LoadingResults"
import { LoadingSensor } from "./LoadingSensor"
import { NoResults } from "./NoResults"

export type ResultListMode = "card" | "row"

type Props<T> = {
  items?: T[]
  component: ComponentType<T & { mode?: ResultListMode }>
  keyField: keyof T
  loading?: boolean
  loadingNextPage?: boolean
  hasNextPage?: boolean
  onFetchMore?: () => void
  mode: ResultListMode
  mobileMode?: ResultListMode
  dynamicSizing?: boolean | "totalDependant"
  getGroupHeading?: (item: T) => string
  renderGroupHeading?: (label: string) => ReactNode
  colSizes?: string
}

const dynamicColSizes = [2, 4, 2, 4, 4, 3, 2, 3, 3, 4, 2, 3]

function dynamicSize(n: number) {
  return `col-span-12 sm:col-span-6 md:col-span-${dynamicColSizes[n % dynamicColSizes.length]}`
}

export function ResultList<T>(props: Props<T>) {
  const dynamicSizing = props.dynamicSizing === true ? true : props.dynamicSizing === "totalDependant" && props.items?.length! >= 8 ? true : false

  let children: ReactNode[] = []

  const getClass = (index: number) =>
    props.mode === "card" ? (dynamicSizing ? dynamicSize(index) : props.colSizes ?? "col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3") : "col-span-12"

  if (props.getGroupHeading) {
    let currentHeading = ""
    let counter = 0
    props.items?.forEach((item, i) => {
      const heading = props.getGroupHeading!(item)
      if (heading !== currentHeading) {
        children.push(
          <h2 key={heading} data-transition="fade" className="col-span-max type-title-s-medium pt-5 pb-5 first:pt-0">
            {props.renderGroupHeading ? props.renderGroupHeading(heading) : heading}
          </h2>
        )
        currentHeading = heading
        counter = 0
      }
      children.push(
        <div data-transition="fade" key={String(item[props.keyField])} className={twMerge("", getClass(counter))}>
          <props.component {...item} mode={props.mode} />
        </div>
      )
      counter++
    })
  } else {
    props.items?.forEach((item, i) => {
      children.push(
        <div data-transition="fade" key={String(item[props.keyField])} className={twMerge("", getClass(i))}>
          <props.component {...item} mode={props.mode} />
        </div>
      )
    })
  }

  // console.log("L", props?.items?.length!, props.items)

  return (
    <div>
      {props?.items?.length! > 0 ? <div className="grid-auto">{children}</div> : props.loading ? <LoadingResults /> : <NoResults />}

      <div data-transition="fade">{props.hasNextPage && <LoadingSensor delay={10} loading={props.loadingNextPage} onSensed={() => props.onFetchMore?.()} />}</div>
    </div>
  )
}

const motionProps: MotionProps = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
}
